import type { ButtonHTMLAttributes, ReactNode } from "react";
import type { Jurisdiction, LegislationKind, SubscriptionType } from "../../convex/model/enums";

const isClassName = (value: string | false | null | undefined): value is string =>
  typeof value === "string" && value !== "";

export const cx = (...a: (string | false | null | undefined)[]): string =>
  a.filter(isClassName).join(" ");

// ---- Icons (simple stroke set) ---------------------------------------------
const PATHS = {
  grid: "M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z",
  book: "M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2zM19 3v16",
  layers: "M12 3 3 8l9 5 9-5zM3 13l9 5 9-5M3 17l9 5 9-5",
  reader: "M6 4h9l4 4v12H6zM15 4v4h4M9 12h7M9 16h7",
  search: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-3.5-3.5",
  bell: "M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6M10 21h4",
  chevron: "M9 6l6 6-6 6",
  chevDown: "M6 9l6 6 6-6",
  check: "M5 12l4 4 10-11",
  plus: "M12 5v14M5 12h14",
  lock: "M7 11V8a5 5 0 0 1 10 0v3M5 11h14v9H5z",
  ext: "M14 5h5v5M19 5l-8 8M11 5H6v13h13v-5",
  spark: "M12 3l1.8 6.2L20 11l-6.2 1.8L12 19l-1.8-6.2L4 11l6.2-1.8z",
  clock: "M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM12 8v4l3 2",
  doc: "M7 3h7l4 4v14H7zM14 3v4h4M10 12h5M10 16h5",
  shield: "M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z",
  users: "M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0zM4 21a8 8 0 0 1 16 0M18 8a3 3 0 0 1 0 6M22 21a6 6 0 0 0-4-5.7M6 8a3 3 0 0 0 0 6M2 21a6 6 0 0 1 4-5.7",
  arrowRight: "M5 12h14M13 6l6 6-6 6",
  filter: "M4 5h16l-6 7v6l-4 2v-8z",
  pencil: "M4 20h4l10-10-4-4L4 16zM14 6l4 4",
  trash: "M5 7h14M9 7V5h6v2M6 7l1 13h10l1-13",
  info: "M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM12 11v5M12 8h.01",
  more: "M5 12h.01M12 12h.01M19 12h.01",
} as const;

export type IconName = keyof typeof PATHS;

export function Icon({
  name,
  size = 18,
  stroke = 1.75,
  className = "",
  fill = false,
}: {
  name: IconName;
  size?: number;
  stroke?: number;
  className?: string;
  fill?: boolean;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d={PATHS[name]} fill={fill ? "currentColor" : "none"} stroke={fill ? "none" : "currentColor"} />
    </svg>
  );
}

// ---- Logo -------------------------------------------------------------------
export function Logo({ size = 20, tone = "default", compact = false }: { size?: number; tone?: "default" | "inverse"; compact?: boolean }) {
  return (
    <div className="flex select-none items-center gap-2.5">
      <svg width={size + 6} height={size + 6} viewBox="0 0 28 28" aria-hidden="true">
        <rect x="1" y="1" width="26" height="26" rx="4" fill="var(--accent)" />
        <path d="M7 8.5h6M7 14h14M7 19.5h10" stroke="#fff" strokeWidth="1.8" strokeLinecap="square" opacity="0.98" />
        <circle cx="20.5" cy="19.5" r="1.5" fill="#7dd3fc" />
      </svg>
      {!compact && (
        <span className={cx("font-semibold tracking-tight", tone === "inverse" ? "text-slate-100" : "text-slate-800")} style={{ fontSize: size }}>
          law<span style={{ color: tone === "inverse" ? "var(--accent-light)" : "var(--accent)" }}>stream</span>
        </span>
      )}
    </div>
  );
}

// ---- Badges -----------------------------------------------------------------
type JurisdictionDisplayCode = Exclude<Jurisdiction, "COM"> | "CTH";

/** Display short code — Commonwealth shows as CTH (our enum stores COM). */
export const jurisShort = (j: Jurisdiction): JurisdictionDisplayCode => (j === "COM" ? "CTH" : j);

const JURIS_TONE: Record<Jurisdiction, string> = {
  COM: "bg-slate-100 text-slate-700 ring-slate-200",
  NSW: "bg-sky-50 text-sky-700 ring-sky-200",
  VIC: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  QLD: "bg-rose-50 text-rose-700 ring-rose-200",
  WA: "bg-amber-50 text-amber-700 ring-amber-200",
  SA: "bg-red-50 text-red-700 ring-red-200",
  TAS: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  ACT: "bg-violet-50 text-violet-700 ring-violet-200",
  NT: "bg-orange-50 text-orange-700 ring-orange-200",
};

export function JurisBadge({ code }: { code: Jurisdiction }) {
  return (
    <span className={cx("inline-flex items-center rounded-[3px] px-1.5 py-0.5 font-mono text-[11px] font-semibold tracking-wide ring-1 ring-inset", JURIS_TONE[code])}>
      {jurisShort(code)}
    </span>
  );
}

export function TypeBadge({ type }: { type: LegislationKind }) {
  return (
    <span className="inline-flex items-center rounded-[3px] bg-white px-1.5 py-0.5 text-[11px] font-medium capitalize text-slate-500 ring-1 ring-inset ring-slate-200">
      {type}
    </span>
  );
}

export function TierBadge({ tier }: { tier: SubscriptionType }) {
  if (tier === "premium")
    return (
      <span className="inline-flex items-center gap-1 rounded-[3px] bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
        <Icon name="spark" size={11} fill /> Premium
      </span>
    );
  return (
    <span className="inline-flex items-center rounded-[3px] bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-500 ring-1 ring-inset ring-slate-200">
      Standard
    </span>
  );
}

export type StatusKind = "in force" | "amended" | "repealed";
export function StatusBadge({ kind }: { kind: StatusKind }) {
  const map: Record<StatusKind, string> = {
    repealed: "bg-slate-100 text-slate-500 ring-slate-200",
    amended: "bg-[var(--accent-soft)] text-[var(--accent-dark)] ring-[color:var(--accent)]/20",
    "in force": "bg-emerald-50 text-emerald-700 ring-emerald-200",
  };
  const label: Record<StatusKind, string> = {
    repealed: "Repealed",
    amended: "Recently amended",
    "in force": "In force",
  };
  return (
    <span className={cx("inline-flex items-center gap-1 rounded-[3px] px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset", map[kind])}>
      {kind === "amended" && <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--accent)" }} />}
      {label[kind]}
    </span>
  );
}

// ---- Button -----------------------------------------------------------------
type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "soft" | "ghost" | "outline";
  size?: "sm" | "md" | "lg";
  icon?: IconName;
  iconRight?: IconName;
};

export function Button({ children, variant = "primary", size = "md", icon, iconRight, className = "", ...rest }: ButtonProps) {
  const sizes = {
    sm: "text-[13px] px-2.5 py-1.5 gap-1.5",
    md: "text-sm px-3.5 py-2 gap-2",
    lg: "text-[15px] px-5 py-2.5 gap-2",
  };
  const variants = {
    primary: "text-white hover:brightness-110 active:brightness-95",
    soft: "bg-[var(--accent-soft)] text-[var(--accent-dark)] hover:bg-[var(--accent-soft)]/70 ring-1 ring-inset ring-[color:var(--accent)]/15",
    ghost: "text-slate-600 hover:bg-slate-100",
    outline: "bg-white text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:ring-slate-400",
  };
  const style = variant === "primary" ? { background: "var(--accent)" } : undefined;
  return (
    <button
      style={style}
      className={cx(
        "inline-flex items-center justify-center rounded font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--signal-cyan)] focus-visible:ring-offset-1 disabled:opacity-40",
        sizes[size],
        variants[variant],
        className,
      )}
      {...rest}
    >
      {icon && <Icon name={icon} size={size === "sm" ? 14 : 16} />}
      {children}
      {iconRight && <Icon name={iconRight} size={size === "sm" ? 14 : 16} />}
    </button>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx("rounded-md border border-slate-200 bg-white shadow-[0_1px_0_rgba(15,23,42,0.04)]", className)}>
      {children}
    </div>
  );
}

export function Panel({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <section className={cx("ls-instrument-panel rounded", className)}>{children}</section>;
}

export function PanelHeader({ eyebrow, title, meta, action, className = "" }: {
  eyebrow?: string;
  title: string;
  meta?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("flex min-h-14 items-center justify-between gap-4 border-b border-slate-200 bg-slate-50/85 px-4 py-2.5", className)}>
      <div className="min-w-0">
        {eyebrow !== undefined && <div className="ls-tech-label text-slate-500">{eyebrow}</div>}
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 className="truncate text-[14px] font-semibold text-slate-900">{title}</h2>
          {meta !== undefined && <div className="shrink-0 text-[12px] text-slate-500">{meta}</div>}
        </div>
      </div>
      {action !== undefined && <div className="shrink-0">{action}</div>}
    </div>
  );
}
